import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  Renderer2,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { Message } from '../models/message.model';
import { ISOTimeToDaysHoursMinutes } from '../shared/helper';
import { ViewImageComponent } from '@app/modules/message-center/view-image/view-image.component';
import { ChatService } from '../services';
import { cdkOverlayClass, ChatMessageStatus, MessageStatus, MessageType } from '../shared/constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationsService } from '@app/core/services';
import { DoIfConfirmed } from '@app/core/decorators';
import { ChatUser } from '@app/modules/message-center/models/user.model';

@UntilDestroy()
@Component({
  selector: 'app-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatMessageComponent {
  message = input<Message>();
  userChatId = input<number>();
  userId = input<number>();
  isThread = input<boolean>(false);
  showDivider = input<boolean>(false);
  showSender = input<boolean>(true);
  threadIsOpened = input<boolean>(false);

  openMessageThread = output<Message>();

  viewImage = viewChild<TemplateRef<any>>('viewImage');

  isMine = computed<boolean>(() => this.message()?.sender?.id === this.userId());
  isInternal = computed<boolean>(() => this.message()?.channel === MessageType.internal);
  isEmail = computed<boolean>(() => this.message()?.channel === MessageType.email);
  isSms = computed<boolean>(() => this.message()?.channel === MessageType.sms);
  sender = computed<ChatUser>(() => this.message()?.sender);
  messageDeliveryStatus = computed<ChatMessageStatus>(() => {
    if (this.message()?.status === MessageStatus.sent) {
      return this.message()?.is_viewed ? ChatMessageStatus.viewed : ChatMessageStatus.delivered;
    }

    return null;
  });
  

  imageMaxHeight: number = 120;
  imageMargin: number = 10;
  resending = signal<boolean>(false);

  readonly MessageStatus = MessageStatus;
  readonly MessageType = MessageType;
  readonly ChatMessageStatus = ChatMessageStatus;

  constructor(
    private dialog: MatDialog,
    private renderer: Renderer2,
    private chatService: ChatService,
    private notificationsService: NotificationsService,
  ) {}

  openImageView(id: number): void {
    this.renderer.addClass(document.body, cdkOverlayClass);
    const files = this.message().files.filter(file => this.isImage(file.content_type));
    this.dialog.open(ViewImageComponent, {
      data: { id, files }
    })
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.renderer.removeClass(document.body, cdkOverlayClass);
      });
  }

  setFilesHeight(): number {
    let images = [];
    let docs = [];
    this.message().files.forEach(file => {
      if (this.isImage(file.content_type)) {
        images.push(file.file);
      }
      if (this.isDocument(file.content_type)) {
        docs.push(file.file);
      }
    });

    let height = 0;
    if (images.length) {
      height += images.length * this.imageMaxHeight + images.length * this.imageMargin;
    }
    if (docs.length) {
      height += 24 * docs.length + this.imageMargin * docs.length - 1;
    }

    return height;
  }

  answerToThread(): void {
    this.openMessageThread.emit(this.message());
  }

  resendMessage(): void {
    this.resending.set(true);
    this.chatService.resendMessage(this.message().id).pipe(
      finalize(() => this.resending.set(false)),
      untilDestroyed(this)
    ).subscribe({
      error: (error) => this.notificationsService.showError(error)
    });
  }

  @DoIfConfirmed({ text: 'messageCenter.deleteMessageConfirmation' })
  deleteMessage(): void {
    this.chatService.deleteMessage(this.message().id)
      .pipe(untilDestroyed(this))
      .subscribe({
        error: (error) => this.notificationsService.showError(error)
      });
  }

  isImage(type: string): boolean {
    return type.startsWith('image/');
  }

  isDocument(type: string): boolean {
    return type.startsWith('application/') || type.startsWith('text/');
  }

  getFormattedTime(time: string): string {
    return ISOTimeToDaysHoursMinutes(time);
  }
}
